import { useMemo, useState } from 'react';
import { AdminFormDialog } from '@/components/shared/AdminFormDialog';
import { Plus, Trash2 } from 'lucide-react';
import { composeRuleCopy } from '@shared/email/commEmailView';
import {
    EXPERIENCE_OPTIONS,
    RULE_ANCHORS,
    RULE_CHANNELS,
    RULE_CONDITION_SIGNALS,
    BILLING_INTERVAL_OPTIONS,
    RULE_SENDER_OPTIONS,
    RULE_SUCCESS,
    RULE_TOPICS,
    WATER_OPTIONS,
} from './commCatalog';
import { CopyGuide, MessagePreview } from './commCopyHelp';
import { buildTemplatePreview, type PreviewTemplateRow } from './commPreview';
import { DestinationField } from './DestinationField';
import { sanitizeCommKey } from './commUi';

export type ConditionRow = {
    signal: string;
    op: 'is_true' | 'is_false' | 'gte' | 'lte' | 'eq' | 'in';
    value: string;
};

export type RuleDraft = {
    key: string;
    name: string;
    description: string;
    is_active: boolean;
    dry_run: boolean;
    anchor: string;
    channel: 'email' | 'push';
    delayAmount: string;
    delayUnit: 'hours' | 'days';
    lookbackHours: string;
    priority: string;
    topic: string;
    cooldownDays: string;
    ignoresCap: boolean;
    conditions: ConditionRow[];
    onlyEmail: string;
    successEvent: string;
    successDays: string;
    subject: string;
    preheader: string;
    body: string;
    imageUrl: string;
    imageAlt: string;
    ctaLabel: string;
    click_url: string;
    cta2Label: string;
    cta2Url: string;
    senderKey: 'alberto' | 'julian';
    recurrenceDays: string;
    maxRepeats: string;
    /** Ancla month_start: día 1–28 Madrid. */
    monthDay: string;
};

export function emptyRuleDraft(): RuleDraft {
    return {
        key: '',
        name: '',
        description: '',
        is_active: false,
        dry_run: false,
        anchor: 'signup',
        channel: 'email',
        delayAmount: '24',
        delayUnit: 'hours',
        lookbackHours: '48',
        priority: '100',
        topic: 'diario',
        cooldownDays: '',
        ignoresCap: false,
        conditions: [],
        onlyEmail: '',
        successEvent: '',
        successDays: '7',
        subject: '',
        preheader: '',
        body: 'Hola {nombre},\n\n',
        imageUrl: '',
        imageAlt: '',
        ctaLabel: 'Abrir AiPeces',
        click_url: '/get-app',
        cta2Label: '',
        cta2Url: '',
        senderKey: 'alberto',
        recurrenceDays: '',
        maxRepeats: '',
        monthDay: '1',
    };
}

export function cooldownHours(draft: RuleDraft): number | null {
    const n = Number(draft.cooldownDays);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n * 24;
}

export function delayHours(draft: RuleDraft): number {
    const n = Number(draft.delayAmount);
    if (!Number.isFinite(n) || n < 0) return 0;
    return draft.delayUnit === 'days' ? n * 24 : n;
}

export function predicateFromConditions(rows: ConditionRow[]): Record<string, unknown> {
    const predicate: Record<string, unknown> = {};
    const all: string[] = [];
    const none: string[] = [];
    const gte: Record<string, number> = {};
    const lte: Record<string, number> = {};
    const eq: Record<string, string> = {};
    const inn: Record<string, string[]> = {};
    for (const row of rows) {
        if (!row.signal) continue;
        if (row.op === 'is_true') all.push(row.signal);
        else if (row.op === 'is_false') none.push(row.signal);
        else if (row.op === 'gte' || row.op === 'lte') {
            const n = Number(row.value);
            if (!Number.isFinite(n)) continue;
            (row.op === 'gte' ? gte : lte)[row.signal] = n;
        } else if (row.op === 'eq' && row.value) {
            eq[row.signal] = row.value;
        } else if (row.op === 'in' && row.value.trim()) {
            const list = row.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
            if (list.length) inn[row.signal] = list;
        }
    }
    if (all.length) predicate.all = all;
    if (none.length) predicate.none = none;
    if (Object.keys(gte).length) predicate.gte = gte;
    if (Object.keys(lte).length) predicate.lte = lte;
    if (Object.keys(eq).length) predicate.eq = eq;
    if (Object.keys(inn).length) predicate.in = inn;
    return predicate;
}

export function audiencePredicate(rows: ConditionRow[], onlyEmail: string): Record<string, unknown> {
    const predicate = predicateFromConditions(rows);
    const email = onlyEmail.trim().toLowerCase();
    if (!email) return predicate;
    const eq = {
        ...((predicate.eq as Record<string, string> | undefined) ?? {}),
        account_email: email,
    };
    return { ...predicate, eq };
}

const fieldClass = 'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm';

function textOptionsForSignal(signal: string) {
    if (signal === 'water_type') return WATER_OPTIONS;
    if (signal === 'billing_interval') return BILLING_INTERVAL_OPTIONS;
    return EXPERIENCE_OPTIONS;
}

export function RuleEditorDialog(props: {
    creating: boolean;
    draft: RuleDraft;
    templates: PreviewTemplateRow[];
    saving: boolean;
    error?: string | null;
    onChange: (next: RuleDraft) => void;
    onCancel: () => void;
    onSave: () => void;
}) {
    const { creating, draft, templates, saving, error, onChange, onCancel, onSave } = props;
    const [initial] = useState(draft);
    const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);
    const isPush = draft.channel === 'push';
    const composedBody = composeRuleCopy({
        text: draft.body,
        primaryLabel: isPush ? '' : draft.ctaLabel,
        secondaryLabel: isPush ? '' : draft.cta2Label,
        secondaryUrl: isPush ? '' : draft.cta2Url,
    });
    const preview = useMemo(
        () =>
            buildTemplatePreview({
                key: draft.key || 'regla',
                channel: draft.channel,
                locale: 'es',
                variant: 'A',
                subject: draft.subject,
                preheader: draft.preheader,
                body: composedBody,
                clickUrl: draft.click_url,
                imageUrl: draft.imageUrl,
                imageAlt: draft.imageAlt,
                templates,
                signature: 'Alberto M., de AiPeces',
            }),
        [composedBody, draft, templates]
    );
    const canSave =
        Boolean(draft.name.trim()) &&
        Boolean(draft.body.trim()) &&
        (isPush ? Boolean(draft.subject.trim()) : Boolean(draft.ctaLabel.trim())) &&
        (!creating || Boolean(sanitizeCommKey(draft.key)));

    const set = (patch: Partial<RuleDraft>) => onChange({ ...draft, ...patch });
    const setCondition = (index: number, patch: Partial<ConditionRow>) => {
        const conditions = draft.conditions.map((row, i) => (i === index ? { ...row, ...patch } : row));
        set({ conditions });
    };

    return (
        <AdminFormDialog
            open
            title={creating ? 'Nueva regla' : draft.name || draft.key}
            description="Cuando se cumple, se manda este correo o aviso (una vez, o cada N días si indicas recurrencia). El correo espera si ya van dos esta semana o no han pasado seis horas. El aviso espera si hoy ya salió otro. Lo urgente no cuenta en ese tope."
            dirty={dirty}
            saving={saving}
            error={error}
            saveDisabled={!canSave}
            maxWidthClass="w-[min(96rem,96vw)] max-w-[min(96rem,96vw)]"
            contentClassName="flex h-[min(92vh,980px)] max-h-[92vh] flex-col overflow-hidden p-6"
            bodyClassName="min-h-0 flex-1 space-y-0 overflow-hidden"
            onClose={onCancel}
            onSave={onSave}
        >
            <div className="grid h-full min-h-0 gap-6 lg:grid-cols-2">
                <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
                    <label className="block text-sm">
                        Nombre
                        <input className={fieldClass} value={draft.name} onChange={(e) => set({ name: e.target.value })} />
                    </label>
                    {creating && (
                        <label className="block text-sm">
                            Clave
                            <input
                                className={fieldClass}
                                value={draft.key}
                                placeholder="bienvenida_24h"
                                onChange={(e) => set({ key: sanitizeCommKey(e.target.value) })}
                            />
                        </label>
                    )}
                    <label className="block text-sm">
                        Canal
                        <select
                            className={fieldClass}
                            value={draft.channel}
                            onChange={(e) => set({ channel: e.target.value as 'email' | 'push' })}
                        >
                            {RULE_CHANNELS.map((channel) => (
                                <option key={channel.key} value={channel.key}>
                                    {channel.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    {!isPush && (
                        <label className="block text-sm">
                            Remitente
                            <select
                                className={fieldClass}
                                value={draft.senderKey}
                                onChange={(e) =>
                                    set({ senderKey: e.target.value as 'alberto' | 'julian' })
                                }
                            >
                                {RULE_SENDER_OPTIONS.map((sender) => (
                                    <option key={sender.key} value={sender.key}>
                                        {sender.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    <div className="grid grid-cols-3 gap-3">
                        <label className="block text-sm">
                            Espera
                            <input
                                className={fieldClass}
                                type="number"
                                min={0}
                                value={draft.delayAmount}
                                onChange={(e) => set({ delayAmount: e.target.value })}
                            />
                        </label>
                        <label className="block text-sm">
                            Unidad
                            <select
                                className={fieldClass}
                                value={draft.delayUnit}
                                onChange={(e) => set({ delayUnit: e.target.value as 'hours' | 'days' })}
                            >
                                <option value="hours">horas</option>
                                <option value="days">días</option>
                            </select>
                        </label>
                        <label className="block text-sm">
                            {draft.anchor === 'renewal_due' ? 'Antes de' : 'Desde'}
                            <select
                                className={fieldClass}
                                value={draft.anchor}
                                onChange={(e) => set({ anchor: e.target.value })}
                            >
                                {RULE_ANCHORS.map((anchor) => (
                                    <option key={anchor.key} value={anchor.key}>
                                        {anchor.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {draft.anchor === 'renewal_due' && (
                        <p className="text-xs text-muted-foreground">
                            La espera cuenta hacia atrás: «3 días» = aviso tres días antes de que renueve Pro.
                        </p>
                    )}
                    {draft.anchor === 'month_start' && (
                        <label className="block text-sm">
                            Día del mes (Madrid, 1–28)
                            <input
                                className={fieldClass}
                                type="number"
                                min={1}
                                max={28}
                                value={draft.monthDay}
                                onChange={(e) => set({ monthDay: e.target.value })}
                            />
                            <span className="mt-1 block text-xs text-muted-foreground">
                                Ese día envía el informe del mes calendario anterior. No se repite el mismo mes
                                Madrid. Cupos normales (2/semana, 6 h) salvo «urgente».
                            </span>
                        </label>
                    )}
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-sm font-medium">Si además…</p>
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm text-primary"
                                onClick={() =>
                                    set({
                                        conditions: [
                                            ...draft.conditions,
                                            { signal: 'capture_count', op: 'lte', value: '0' },
                                        ],
                                    })
                                }
                            >
                                <Plus size={14} /> Condición
                            </button>
                        </div>
                        <div className="space-y-2">
                            {draft.conditions.length === 0 && (
                                <p className="text-sm text-muted-foreground">Sin condiciones extra: basta con la espera.</p>
                            )}
                            {draft.conditions.map((row, index) => {
                                const signal = RULE_CONDITION_SIGNALS.find((s) => s.key === row.signal);
                                const kind = signal?.kind ?? 'num';
                                return (
                                    <div key={index} className="grid grid-cols-[1fr_auto_auto_auto] gap-2">
                                        <select
                                            className={fieldClass}
                                            value={row.signal}
                                            onChange={(e) => {
                                                const next = RULE_CONDITION_SIGNALS.find((s) => s.key === e.target.value);
                                                const op =
                                                    next?.kind === 'bool'
                                                        ? 'is_true'
                                                        : next?.kind === 'text'
                                                          ? 'eq'
                                                          : 'lte';
                                                setCondition(index, { signal: e.target.value, op, value: '' });
                                            }}
                                        >
                                            {RULE_CONDITION_SIGNALS.map((s) => (
                                                <option key={s.key} value={s.key}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            className={fieldClass}
                                            value={row.op}
                                            onChange={(e) => setCondition(index, { op: e.target.value as ConditionRow['op'] })}
                                        >
                                            {kind === 'bool' && (
                                                <>
                                                    <option value="is_true">sí</option>
                                                    <option value="is_false">no</option>
                                                </>
                                            )}
                                            {kind === 'num' && (
                                                <>
                                                    <option value="lte">como máximo</option>
                                                    <option value="gte">como mínimo</option>
                                                </>
                                            )}
                                            {kind === 'text' && (
                                                <>
                                                    <option value="eq">es</option>
                                                    <option value="in">es uno de</option>
                                                </>
                                            )}
                                        </select>
                                        {kind === 'num' && (
                                            <input
                                                className={fieldClass}
                                                type="number"
                                                value={row.value}
                                                onChange={(e) => setCondition(index, { value: e.target.value })}
                                            />
                                        )}
                                        {kind === 'text' && row.op !== 'in' && (
                                            <select
                                                className={fieldClass}
                                                value={row.value}
                                                onChange={(e) => setCondition(index, { value: e.target.value })}
                                            >
                                                <option value="">—</option>
                                                {textOptionsForSignal(row.signal).map((opt) => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {kind === 'text' && row.op === 'in' && (
                                            <select
                                                className={fieldClass}
                                                multiple
                                                size={3}
                                                value={row.value.split(',').filter(Boolean)}
                                                onChange={(e) => {
                                                    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                                                    setCondition(index, { value: selected.join(',') });
                                                }}
                                            >
                                                {textOptionsForSignal(row.signal).map((opt) => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {kind === 'bool' && <span />}
                                        <button
                                            type="button"
                                            className="mt-1 text-muted-foreground"
                                            onClick={() =>
                                                set({ conditions: draft.conditions.filter((_, i) => i !== index) })
                                            }
                                            aria-label="Quitar condición"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <label className="block text-sm">
                        Solo este correo
                        <input
                            className={fieldClass}
                            type="email"
                            value={draft.onlyEmail}
                            placeholder="tu@correo.com"
                            onChange={(e) => set({ onlyEmail: e.target.value })}
                        />
                        <span className="mt-1 block text-xs text-muted-foreground">
                            Rellénalo para que la regla salga solo a esa cuenta. Así puedes probar el flujo contigo.
                        </span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            Objetivo
                            <select
                                className={fieldClass}
                                value={draft.successEvent}
                                onChange={(e) => set({ successEvent: e.target.value })}
                            >
                                {RULE_SUCCESS.map((item) => (
                                    <option key={item.key || 'none'} value={item.key}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block text-sm">
                            Plazo del objetivo (días)
                            <input
                                className={fieldClass}
                                type="number"
                                min={1}
                                disabled={!draft.successEvent}
                                value={draft.successDays}
                                onChange={(e) => set({ successDays: e.target.value })}
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            Prioridad (menor sale antes)
                            <input
                                className={fieldClass}
                                type="number"
                                value={draft.priority}
                                onChange={(e) => set({ priority: e.target.value })}
                            />
                        </label>
                        <label className="block text-sm">
                            Solo quien cruzó el umbral en las últimas (horas)
                            <input
                                className={fieldClass}
                                type="number"
                                min={1}
                                value={draft.lookbackHours}
                                onChange={(e) => set({ lookbackHours: e.target.value })}
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            Tema
                            <select
                                className={fieldClass}
                                value={draft.topic}
                                onChange={(e) => set({ topic: e.target.value })}
                            >
                                {RULE_TOPICS.map((topic) => (
                                    <option key={topic.key} value={topic.key}>
                                        {topic.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block text-sm">
                            Si ya recibió este tema, esperar (días)
                            <input
                                className={fieldClass}
                                type="number"
                                min={0}
                                placeholder="0"
                                value={draft.cooldownDays}
                                onChange={(e) => set({ cooldownDays: e.target.value })}
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            Repetir cada (días)
                            <input
                                className={fieldClass}
                                type="number"
                                min={0}
                                placeholder="Vacío = una sola vez"
                                value={draft.recurrenceDays}
                                onChange={(e) => set({ recurrenceDays: e.target.value })}
                            />
                        </label>
                        <label className="block text-sm">
                            Envíos como máximo, contando el primero
                            <input
                                className={fieldClass}
                                type="number"
                                min={0}
                                placeholder="Vacío = sin límite"
                                value={draft.maxRepeats}
                                onChange={(e) => set({ maxRepeats: e.target.value })}
                            />
                        </label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Tras el primer envío, vuelve a evaluarse cada N días (topes de correo incluidos). El máximo
                        cuenta todos los envíos de la regla para ese usuario.
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Déjalo en 0 si el correo y el aviso del mismo momento deben salir juntos, como al activar o comprar Pro.
                    </p>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={draft.dry_run}
                            onChange={(e) => set({ dry_run: e.target.checked })}
                        />
                        En prueba (reserva a quién le tocaría, no envía)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={draft.is_active}
                            onChange={(e) => set({ is_active: e.target.checked })}
                        />
                        Regla encendida
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={draft.ignoresCap}
                            onChange={(e) => set({ ignoresCap: e.target.checked })}
                        />
                        Urgente: no cuenta en el tope (activar o comprar Pro)
                    </label>
                    <label className="block text-sm">
                        {isPush ? 'Título' : 'Asunto'}
                        <input className={fieldClass} value={draft.subject} onChange={(e) => set({ subject: e.target.value })} />
                    </label>
                    {!isPush && (
                        <label className="block text-sm">
                            Preencabezado
                            <input
                                className={fieldClass}
                                value={draft.preheader}
                                onChange={(e) => set({ preheader: e.target.value })}
                            />
                        </label>
                    )}
                    <label className="block text-sm">
                        {isPush ? 'Texto del aviso' : 'Cuerpo'}
                        <textarea
                            className={`${fieldClass} min-h-40 font-mono`}
                            value={draft.body}
                            onChange={(e) => set({ body: e.target.value })}
                        />
                    </label>
                    <CopyGuide channel={isPush ? 'push' : 'email'} />
                    <label className="block text-sm">
                        Imagen (https)
                        <input
                            className={fieldClass}
                            value={draft.imageUrl}
                            placeholder="https://..."
                            onChange={(e) => set({ imageUrl: e.target.value })}
                        />
                    </label>
                    {!isPush && draft.imageUrl.trim() && (
                        <label className="block text-sm">
                            Texto alternativo de la imagen
                            <input
                                className={fieldClass}
                                value={draft.imageAlt}
                                placeholder="Describe la imagen para lectores de pantalla"
                                onChange={(e) => set({ imageAlt: e.target.value })}
                            />
                            <span className="mt-1 block text-xs text-muted-foreground">
                                Déjalo vacío si la imagen es solo decorativa.
                            </span>
                        </label>
                    )}
                    {!isPush && (
                        <>
                            <label className="block text-sm">
                                Botón
                                <input
                                    className={fieldClass}
                                    value={draft.ctaLabel}
                                    onChange={(e) => set({ ctaLabel: e.target.value })}
                                />
                            </label>
                            <DestinationField
                                label="Destino"
                                value={draft.click_url}
                                onChange={(click_url) => set({ click_url })}
                                inputClassName={fieldClass}
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block text-sm">
                                    Segundo botón
                                    <input
                                        className={fieldClass}
                                        value={draft.cta2Label}
                                        onChange={(e) => set({ cta2Label: e.target.value })}
                                    />
                                </label>
                                <DestinationField
                                    label="Destino del segundo botón"
                                    value={draft.cta2Url}
                                    onChange={(cta2Url) => set({ cta2Url })}
                                    inputClassName={fieldClass}
                                />
                            </div>
                        </>
                    )}
                    {isPush && (
                        <DestinationField
                            label="Destino (al pulsarlo abre)"
                            value={draft.click_url}
                            onChange={(click_url) => set({ click_url })}
                            inputClassName={fieldClass}
                        />
                    )}
                </div>
                <MessagePreview
                    channel={isPush ? 'push' : 'email'}
                    subject={preview.subject}
                    bodyText={preview.bodyText}
                    html={preview.html}
                    missing={preview.missing}
                    clickUrl={draft.click_url}
                />
            </div>
        </AdminFormDialog>
    );
}
